// File: src/state/Timeline/timeline.reducer.ts
import type {
  TimelineAction,
  TimelineAnnotation,
  TimelineAnnotationKind,
  TimelineAnnotationProvenance,
  TimelineAnnotationProposedAction,
  TimelineAnnotationSeverity,
  TimelineAnnotationSubtype,
  TimelineClip,
  TimelineMarker,
  TimelineRange,
  TimelineState,
} from "./timeline.types";

/* ======================================================
 * Helpers
 * ====================================================== */

const MIN_RANGE_LEN = 0.05;

// ✅ Runtime kind allowlist (includes "phase")
const ALLOWED_RANGE_KINDS = new Set<string>([
  "attention",
  "overload",
  "emotion",
  "music",
  "selection",
  "loop",
  "safety",
  "phase",

  // aliases (legacy/UI may emit)
  "safe",
  "danger",
]);

// TimelineSnap.mode includes "marker" in your types
const SNAP_MODES = new Set<string>(["second", "frame", "beat", "marker"]);

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function finite(n: unknown): n is number {
  return Number.isFinite(n as number);
}

function isPhaseLabel(label: unknown): boolean {
  const s = String(label ?? "").trim().toLowerCase();
  return s.startsWith("phase:");
}

/**
 * Normalize a raw kind into a canonical kind.
 * - "safe"   -> "safety"
 * - "danger" -> "overload"
 * - unknown  -> undefined
 */
function normalizeKind(raw: unknown): string | undefined {
  if (raw == null) return undefined;

  const s = String(raw).toLowerCase().trim();
  if (!s) return undefined;

  if (s === "safe") return "safety";
  if (s === "danger") return "overload";

  return ALLOWED_RANGE_KINDS.has(s) ? s : undefined;
}

function normStartEnd(
  startSec: unknown,
  endSec: unknown,
  durationSec: number,
): { startSec: number; endSec: number } {
  const a0 = finite(startSec) ? Number(startSec) : 0;
  const b0 = finite(endSec) ? Number(endSec) : a0;

  let lo = Math.min(a0, b0);
  let hi = Math.max(a0, b0);

  lo = clamp(lo, 0, durationSec);
  hi = clamp(hi, 0, durationSec);

  // Enforce a minimal range length (stable UX + avoids degenerate math)
  if (hi - lo < MIN_RANGE_LEN) {
    hi = clamp(lo + MIN_RANGE_LEN, 0, durationSec);
    if (hi - lo < MIN_RANGE_LEN) lo = clamp(hi - MIN_RANGE_LEN, 0, durationSec);
  }

  if (hi < lo) hi = lo;

  return { startSec: lo, endSec: hi };
}

function replaceById<T extends { id: string }>(xs: T[], next: T): T[] {
  const i = xs.findIndex((x) => x.id === next.id);
  if (i < 0) return [...xs, next];
  const copy = xs.slice();
  copy[i] = next;
  return copy;
}

function readStr(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const s = v.trim();
  return s.length ? s : undefined;
}

function readBool(v: unknown): boolean | undefined {
  return typeof v === "boolean" ? v : undefined;
}

function readFrameIndex(v: unknown): number | null | undefined {
  if (v == null) return v === null ? null : undefined;
  if (!finite(v)) return undefined;
  return Math.max(0, Math.round(Number(v)));
}

function coerceRangeAnchorBinding(raw: unknown): TimelineClip["rangeAnchor"] {
  if (!raw || typeof raw !== "object") return null;

  const rangeId = readStr((raw as Record<string, unknown>).rangeId);
  if (!rangeId) return null;

  return {
    source: "marker_span",
    rangeId,
    startMarkerId: readStr((raw as Record<string, unknown>).startMarkerId) ?? null,
    endMarkerId: readStr((raw as Record<string, unknown>).endMarkerId) ?? null,
    mode: "span",
    followUpdates: readBool((raw as Record<string, unknown>).followUpdates) ?? true,
    boundAt: finite((raw as Record<string, unknown>).boundAt)
      ? Number((raw as Record<string, unknown>).boundAt)
      : null,
  };
}

function coerceRangeMarkerSpan(raw: unknown): TimelineRange["markerSpan"] {
  if (!raw || typeof raw !== "object") return null;

  const source = readStr((raw as Record<string, unknown>).source);
  const rawMarkerIds = Array.isArray((raw as Record<string, unknown>).markerIds)
    ? ((raw as Record<string, unknown>).markerIds as unknown[])
    : [];
  const markerIds = Array.from(new Set(rawMarkerIds.map((value) => String(value ?? "").trim()).filter(Boolean)));
  const startMarkerId = readStr((raw as Record<string, unknown>).startMarkerId) ?? markerIds[0] ?? null;
  const endMarkerId = readStr((raw as Record<string, unknown>).endMarkerId) ?? markerIds[markerIds.length - 1] ?? null;

  if (!markerIds.length && !startMarkerId && !endMarkerId) return null;

  return {
    source: source === "marker_span" ? "marker_span" : "marker_span",
    markerIds,
    startMarkerId,
    endMarkerId,
    markerType: readStr((raw as Record<string, unknown>).markerType) ?? null,
    markerOrigin: readStr((raw as Record<string, unknown>).markerOrigin) ?? null,
    promotedAt: finite((raw as Record<string, unknown>).promotedAt)
      ? Number((raw as Record<string, unknown>).promotedAt)
      : null,
  };
}

function coerceAnnotationProvenance(raw: unknown): TimelineAnnotationProvenance | null {
  if (!raw || typeof raw !== "object") return null;
  const source = raw as Record<string, unknown>;

  return {
    authoringSource: (readStr(source.authoringSource) ?? null) as TimelineAnnotationProvenance["authoringSource"],
    captureSource: (readStr(source.captureSource) ?? null) as TimelineAnnotationProvenance["captureSource"],
    suggestionId: readStr(source.suggestionId) ?? null,
    suggestionSource: (readStr(source.suggestionSource) ?? null) as TimelineAnnotationProvenance["suggestionSource"],
    suggestionDisposition: (readStr(source.suggestionDisposition) ?? null) as TimelineAnnotationProvenance["suggestionDisposition"],
    provider: readStr(source.provider) ?? null,
    confidence: finite(source.confidence) ? Number(source.confidence) : null,
    generatedAt: finite(source.generatedAt) ? Number(source.generatedAt) : null,
    originalSuggestionText: readStr(source.originalSuggestionText) ?? null,
  };
}

function coerceAnnotation(raw: unknown, durationSec: number, metaTs?: number, existing?: TimelineAnnotation): TimelineAnnotation | null {
  if (!raw || typeof raw !== "object") return null;

  const source = raw as Record<string, unknown>;
  const id = String(source.id ?? existing?.id ?? "").trim();
  const rawNote = String(source.rawNote ?? existing?.rawNote ?? "").trim();
  if (!id || !rawNote) return null;

  const createdAt = finite(source.createdAt)
    ? Number(source.createdAt)
    : finite(existing?.createdAt)
      ? Number(existing?.createdAt)
      : finite(metaTs)
        ? Number(metaTs)
        : 0;
  const updatedAt = finite(source.updatedAt)
    ? Number(source.updatedAt)
    : finite(metaTs)
      ? Number(metaTs)
      : finite(existing?.updatedAt)
        ? Number(existing?.updatedAt)
        : createdAt;
  const nextAction = readStr(source.nextAction) ?? readStr(source.action) ?? existing?.nextAction ?? existing?.action ?? null;
  const markerVisible = typeof source.markerVisible === "boolean"
    ? source.markerVisible
    : typeof existing?.markerVisible === "boolean"
      ? existing.markerVisible
      : true;
  const provenance = coerceAnnotationProvenance(source.provenance) ?? existing?.provenance ?? null;

  return {
    id,
    createdAt,
    updatedAt,
    type: String(source.type ?? existing?.type ?? "creative") as TimelineAnnotationKind,
    subtype: (String(source.subtype ?? existing?.subtype ?? "").trim() || null) as TimelineAnnotationSubtype | null,
    severity: (String(source.severity ?? existing?.severity ?? "").trim() || null) as TimelineAnnotationSeverity | null,
    summary: String(source.summary ?? existing?.summary ?? rawNote).trim() || rawNote,
    details: source.details && typeof source.details === "object" ? (source.details as TimelineAnnotation["details"]) : existing?.details ?? null,
    proposedAction: (String(source.proposedAction ?? existing?.proposedAction ?? "").trim() || null) as TimelineAnnotationProposedAction | null,
    nodeId: String(source.nodeId ?? existing?.nodeId ?? "").trim() || null,
    assetLabel: String(source.assetLabel ?? existing?.assetLabel ?? "active clip").trim() || "active clip",
    clipId: String(source.clipId ?? existing?.clipId ?? "").trim() || null,
    linkedAudio: typeof source.linkedAudio === "boolean" ? source.linkedAudio : !!existing?.linkedAudio,
    frameIndex: finite(source.frameIndex) ? Math.max(0, Number(source.frameIndex)) : Math.max(0, Number(existing?.frameIndex ?? 0)),
    anchorTimecode: String(source.anchorTimecode ?? existing?.anchorTimecode ?? "00:00:00:00").trim() || "00:00:00:00",
    timeSec: finite(source.timeSec)
      ? clamp(Number(source.timeSec), 0, durationSec)
      : finite(existing?.timeSec)
        ? clamp(Number(existing?.timeSec), 0, durationSec)
        : null,
    rawNote,
    nextAction,
    markerVisible,
    provenance,
    action: nextAction,
    source: source.source === "voice" ? "voice" : source.source === "ai" ? "ai" : source.source === "manual" ? "manual" : existing?.source ?? "manual",
  };
}

/* ======================================================
 * Tracks + overlap policy (strict-first)
 * ====================================================== */

type TrackOverlapMode = "strict" | "soft-resolve" | "multi-layer";

function getTrackOverlapMode(state: TimelineState, trackId: string): TrackOverlapMode {
  const t = (state.tracks ?? []).find((x: any) => String(x?.id) === String(trackId));
  const m = String((t as any)?.overlapMode ?? "strict").trim().toLowerCase();
  if (m === "soft" || m === "softresolve" || m === "soft-resolve") return "soft-resolve";
  if (m === "multi" || m === "multilayer" || m === "multi-layer") return "multi-layer";
  return "strict";
}

function overlaps(a: { startSec: number; endSec: number }, b: { startSec: number; endSec: number }): boolean {
  // strict interval overlap (touching at boundary is OK)
  return a.startSec < b.endSec && b.startSec < a.endSec;
}

function sortByStartThenId<T extends { startSec: number; id: string }>(xs: T[]): T[] {
  const copy = xs.slice();
  copy.sort((p, q) => {
    const d = p.startSec - q.startSec;
    if (Math.abs(d) > 1e-9) return d;
    return String(p.id).localeCompare(String(q.id));
  });
  return copy;
}

/**
 * Strict overlap enforcement:
 * - Deterministic "push-forward incoming" on the same track
 * - If overflow, push-back to last fit window
 * - Never changes duration (len preserved), only start/end
 */
function enforceStrictNoOverlapForClip(state: TimelineState, clip: TimelineClip, allClips: TimelineClip[]): TimelineClip {
  const mode = getTrackOverlapMode(state, clip.trackId);
  if (mode !== "strict") return clip;

  const durationSec = state.durationSec;
  const len = Math.max(MIN_RANGE_LEN, clip.endSec - clip.startSec);

  const others = (allClips ?? [])
    .filter((c) => String(c.trackId) === String(clip.trackId) && String(c.id) !== String(clip.id))
    .map((c) => coerceClip(c as any, durationSec))
    .filter(Boolean) as TimelineClip[];

  if (!others.length) return clip;

  // Push forward until no overlaps remain (bounded loop)
  let startSec = clip.startSec;
  let endSec = startSec + len;

  const sorted = sortByStartThenId(others);

  for (let guard = 0; guard < 64; guard++) {
    const hit = sorted.find((c) => overlaps({ startSec, endSec }, c));
    if (!hit) break;

    // push incoming to the end of the first overlapping clip
    startSec = hit.endSec;
    endSec = startSec + len;

    // If we blow past duration, we stop and handle below
    if (endSec > durationSec) break;
  }

  // If overflow, push back to fit; then ensure still non-overlapping by scanning backward
  if (endSec > durationSec) {
    endSec = clamp(durationSec, 0, durationSec);
    startSec = clamp(endSec - len, 0, Math.max(0, durationSec - MIN_RANGE_LEN));
    endSec = clamp(startSec + len, startSec + MIN_RANGE_LEN, durationSec);

    // Now ensure no overlap by moving backwards if needed (deterministic)
    // Scan overlaps with any clip that intersects; snap our end to their start.
    // Iterate until stable.
    for (let guard = 0; guard < 64; guard++) {
      const hit = sorted
        .slice()
        .reverse()
        .find((c) => overlaps({ startSec, endSec }, c));
      if (!hit) break;

      endSec = hit.startSec;
      startSec = endSec - len;

      // clamp to timeline bounds
      if (startSec < 0) {
        startSec = 0;
        endSec = clamp(startSec + len, startSec + MIN_RANGE_LEN, durationSec);
        break;
      }

      endSec = clamp(endSec, 0, durationSec);
      startSec = clamp(startSec, 0, Math.max(0, durationSec - MIN_RANGE_LEN));
    }
  }

  const norm = normStartEnd(startSec, endSec, durationSec);
  return { ...clip, ...norm };
}

function enforceStrictNoOverlapAll(state: TimelineState, clips: TimelineClip[]): TimelineClip[] {
  // Only enforce on tracks that are strict; keep deterministic ordering.
  const durationSec = state.durationSec;

  const byTrack = new Map<string, TimelineClip[]>();
  for (const c0 of clips ?? []) {
    const c = coerceClip(c0 as any, durationSec) ?? c0;
    const tid = String((c as any).trackId ?? "");
    if (!tid) continue;
    const arr = byTrack.get(tid) ?? [];
    arr.push(c as TimelineClip);
    byTrack.set(tid, arr);
  }

  const out: TimelineClip[] = [];
  for (const [trackId, list0] of byTrack.entries()) {
    const mode = getTrackOverlapMode(state, trackId);
    if (mode !== "strict") {
      out.push(...list0);
      continue;
    }

    // Deterministic: sort by start then id, then push-forward subsequent clips.
    const list = sortByStartThenId(list0);
    const placed: TimelineClip[] = [];

    for (const c of list) {
      const fixed = enforceStrictNoOverlapForClip(
        state,
        c,
        placed, // only compare against already placed to ensure canonical packing
      );
      placed.push(fixed);
    }

    out.push(...placed);
  }

  // Preserve original ids set, but return in original-ish order:
  // - stable by trackId then startSec then id
  return out.sort((p, q) => {
    const td = String(p.trackId).localeCompare(String(q.trackId));
    if (td) return td;
    const sd = p.startSec - q.startSec;
    if (Math.abs(sd) > 1e-9) return sd;
    return String(p.id).localeCompare(String(q.id));
  });
}

/* ======================================================
 * Clip/Marker/Range coercion
 * ====================================================== */

/**
 * TimelineClip coercion:
 * - Canonical: { startSec, endSec }
 * - Legacy-friendly: { startSec, durSec } => endSec = startSec + durSec
 *
 * Phase M1 quality:
 * - Normalize url/mediaId/previewUrl strings (trim; empty => undefined)
 */
function coerceClip(raw: any, durationSec: number): TimelineClip | null {
  if (!raw) return null;
  const id = String(raw.id ?? "");
  const trackId = String(raw.trackId ?? "");
  if (!id || !trackId) return null;

  const start0 = finite(raw.startSec) ? Number(raw.startSec) : 0;

  // canonical endSec
  let end0: number | null = finite(raw.endSec) ? Number(raw.endSec) : null;

  // legacy durSec
  if (end0 == null && finite(raw.durSec)) {
    end0 = start0 + Math.max(MIN_RANGE_LEN, Number(raw.durSec));
  }

  const { startSec, endSec } = normStartEnd(start0, end0 ?? start0 + 3, durationSec);

  const url = readStr(raw.url);
  const mediaId = readStr(raw.mediaId);
  const previewUrl = readStr(raw.previewUrl);
  const requestId = readStr(raw.requestId);

  const kindRaw = typeof raw.kind === "string" ? String(raw.kind) : "";
  const kind: TimelineClip["kind"] =
    kindRaw === "audio" || kindRaw === "video" || kindRaw === "npc" ? (kindRaw as any) : "video";

  const next: TimelineClip = {
    ...raw,
    id,
    trackId,
    startSec,
    endSec,
    kind,
    ...(url ? { url } : { url: undefined }),
    ...(previewUrl ? { previewUrl } : { previewUrl: undefined }),
    ...(mediaId ? { mediaId } : { mediaId: undefined }),
    ...(requestId ? { requestId } : { requestId: undefined }),
    rangeAnchor: coerceRangeAnchorBinding(raw.rangeAnchor) ?? null,
  };

  return next;
}

function coerceMarker(raw: any, durationSec: number): TimelineMarker | null {
  if (!raw) return null;
  const id = String(raw.id ?? "");
  if (!id) return null;

  const timeSec = clamp(finite(raw.timeSec) ? Number(raw.timeSec) : 0, 0, durationSec);
  const label = readStr(raw.label);
  const annotationType = readStr(raw.annotation?.type);
  const type = readStr(raw.type) ?? annotationType ?? (isPhaseLabel(label) ? "phase" : undefined);
  const note = readStr(raw.note) ?? readStr(raw.description) ?? readStr(raw.annotation?.summary) ?? readStr(raw.annotation?.rawNote);
  const description = readStr(raw.description) ?? note;
  const color = raw.color === null ? null : readStr(raw.color);
  const frameIndex = readFrameIndex(raw.frameIndex) ?? readFrameIndex(raw.annotation?.frameIndex);
  const visible = readBool(raw.visible) ?? readBool(raw.annotation?.markerVisible);

  const next: TimelineMarker = {
    ...raw,
    id,
    timeSec,
    label,
    type,
    color,
    note: note ?? null,
    frameIndex: frameIndex ?? null,
    visible,
    description: description ?? null,
  };

  return next;
}

function coerceRange(raw: any, durationSec: number): TimelineRange | null {
  if (!raw) return null;
  const id = String(raw.id ?? "");
  if (!id) return null;

  const { startSec, endSec } = normStartEnd(raw.startSec, raw.endSec, durationSec);

  const kind = isPhaseLabel(raw.label) ? "phase" : normalizeKind(raw.kind) ?? "selection";

  const next: TimelineRange = {
    ...raw,
    id,
    startSec,
    endSec,
    kind: kind as any,
    markerSpan: coerceRangeMarkerSpan(raw.markerSpan) ?? null,
  };

  return next;
}

function applyRangeFollowers(state: TimelineState, range: TimelineRange, clipsInput?: TimelineClip[]): TimelineClip[] {
  const startSec = Math.max(0, Math.min(Number(range.startSec ?? 0), Number(range.endSec ?? 0)));
  const endSec = Math.max(startSec, Math.max(Number(range.startSec ?? 0), Number(range.endSec ?? 0)));
  const clipsBase = Array.isArray(clipsInput) ? clipsInput.slice() : (state.clips ?? []).slice();

  let changed = false;
  const clips = clipsBase.map((clip) => {
    const anchor = coerceRangeAnchorBinding((clip as any)?.rangeAnchor);
    if (!anchor || String(anchor.rangeId) !== String(range.id) || anchor.followUpdates === false) return clip;

    const next = coerceClip(
      {
        ...clip,
        startSec,
        endSec,
        durSec: Math.max(0, endSec - startSec),
        rangeAnchor: anchor,
      },
      state.durationSec,
    ) ?? clip;

    if (next.startSec !== clip.startSec || next.endSec !== clip.endSec || next.durSec !== clip.durSec) {
      changed = true;
    }

    return next;
  });

  return changed ? enforceStrictNoOverlapAll(state, clips) : clipsBase;
}

/* ======================================================
 * AtomicRuns coercion (authoritative)
 * ====================================================== */

type AtomicRunRaw = any;

function coerceAtomicRun(raw: AtomicRunRaw, durationSec: number, tsFallback?: number) {
  if (!raw) return null;

  const id = String(raw.id ?? "").trim();
  if (!id) return null;

  const label = String(raw.label ?? raw.title ?? "").trim() || `Run ${id}`;

  const start0 = finite(raw.startSec) ? Number(raw.startSec) : 0;
  const dur0 = finite(raw.durSec) ? Number(raw.durSec) : 0;

  const durSec = clamp(
    Math.max(MIN_RANGE_LEN, dur0 > 0 ? dur0 : MIN_RANGE_LEN),
    MIN_RANGE_LEN,
    Math.max(MIN_RANGE_LEN, durationSec),
  );

  const startSec = clamp(start0, 0, Math.max(0, durationSec - durSec));
  const notes = raw.notes ?? undefined;

  const updatedAt = finite(raw.updatedAt) ? Number(raw.updatedAt) : finite(tsFallback) ? Number(tsFallback) : 0;

  return {
    ...raw,
    id,
    label,
    startSec,
    durSec,
    notes,
    v: 1 as const,
    updatedAt,
  };
}

/* ======================================================
 * Default State
 * ====================================================== */

export const DEFAULT_TIMELINE_STATE: TimelineState = {
  durationSec: 120,
  playheadSec: 0,
  viewport: {
    startSec: 0,
    endSec: 15,
    pxPerSec: 100,
  },
  laneFocus: {
    mode: "all",
    visibleTrackIds: [],
    anchorTrackId: null,
    hideAuxiliaryLanes: false,
    sourceClipId: null,
  },
  snap: {
    enabled: false,
    mode: "second",
    fps: 30,
    bpm: 120,
    beatDiv: 1,
  },

  // ✅ Canonical tracks (required)
  tracks: [
    { id: "v1", name: "Video 1", kind: "video", height: 72, overlapMode: "strict" },
    { id: "a1", name: "Audio 1", kind: "audio", height: 56, overlapMode: "strict" },
    { id: "npc1", name: "NPC 1", kind: "npc", height: 56, overlapMode: "strict" },
  ],

  markers: [],
  annotations: [],
  clips: [],
  ranges: [],

  selectedClipId: null,
  selectedRangeId: null,
  loopSelectionEnabled: false,

  atomicRuns: {
    v: 1,
    updatedAt: 0, // ✅ reducer won’t stamp time; actions should.
    runs: [],
    selectedRunId: null,
  },

  narrativePreview: {
    active: false,
    compileId: null,
    blocks: [],
    overlays: [],
  },
};

/* ======================================================
 * Reducer
 * ====================================================== */

export function timelineReducer(state: TimelineState, action: TimelineAction): TimelineState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const a: any = action as any;

  // Prefer meta.ts if provided; reducer never calls Date.now()
  const ts = finite(a?.meta?.ts) ? Number(a.meta.ts) : undefined;

  switch (action.type) {
    /* ---------- Timeline ---------- */

    case "timeline/setDuration": {
      const durRaw = action.payload?.durationSec;
      const durationSec = finite(durRaw) ? Math.max(0.1, Number(durRaw)) : state.durationSec;

      const playheadSec = clamp(state.playheadSec, 0, durationSec);

      const viewportStart = clamp(state.viewport.startSec, 0, durationSec);
      const viewportEnd = clamp(state.viewport.endSec, viewportStart, durationSec);

      const markers = (state.markers ?? []).map((m) => ({
        ...m,
        timeSec: clamp(finite(m.timeSec) ? Number(m.timeSec) : 0, 0, durationSec),
      }));

      // Coerce entities to fit new duration
      let clips = (state.clips ?? []).map((c) => coerceClip(c, durationSec)).filter(Boolean) as TimelineClip[];
      const ranges = (state.ranges ?? []).map((r) => coerceRange(r, durationSec)).filter(Boolean) as TimelineRange[];

      // Enforce strict overlap packing after duration shrink (only impacts strict tracks)
      clips = enforceStrictNoOverlapAll({ ...state, durationSec }, clips);

      const runs = (state.atomicRuns?.runs ?? [])
        .map((r: any) => coerceAtomicRun(r, durationSec, ts))
        .filter(Boolean);

      const selectedRunIdRaw =
        typeof state.atomicRuns?.selectedRunId === "string" ? state.atomicRuns.selectedRunId : null;

      const selectedRunId =
        selectedRunIdRaw && runs.some((r: any) => String(r?.id) === selectedRunIdRaw) ? selectedRunIdRaw : null;

      const atomicRuns = {
        ...state.atomicRuns,
        runs,
        selectedRunId,
        updatedAt: finite(ts) ? ts : (finite(state.atomicRuns?.updatedAt) ? Number(state.atomicRuns.updatedAt) : 0),
      };

      const selectedClipId =
        state.selectedClipId && clips.some((c) => String(c.id) === String(state.selectedClipId))
          ? state.selectedClipId
          : null;

      const selectedRangeId =
        state.selectedRangeId && ranges.some((r) => String(r.id) === String(state.selectedRangeId))
          ? state.selectedRangeId
          : null;

      return {
        ...state,
        durationSec,
        playheadSec,
        viewport: { ...state.viewport, startSec: viewportStart, endSec: viewportEnd },
        markers,
        clips,
        ranges,
        atomicRuns,
        selectedClipId,
        selectedRangeId,
      };
    }

    case "timeline/setPlayhead": {
      // BLOCK: HARDENED_SET_PLAYHEAD
      // Keep logging for dev visibility and accept multiple payload shapes.
      try {
        // eslint-disable-next-line no-console
        console.log("🔥 SET PLAYHEAD ACTION", (action as any).payload);
      } catch {}

      const next =
        (action as any).payload?.playheadSec ??
        (action as any).payload?.timeSec ??
        (action as any).payload?.time ??
        0;

      return {
        ...state,
        playheadSec: Number(next) || 0,
      };
    }

    case "timeline/setViewport": {
      const aRaw = action.payload?.startSec;
      const bRaw = action.payload?.endSec;

      const a0 = finite(aRaw) ? Number(aRaw) : state.viewport.startSec;
      const b0 = finite(bRaw) ? Number(bRaw) : state.viewport.endSec;

      const startSec = clamp(Math.min(a0, b0), 0, state.durationSec);
      const endSec = clamp(Math.max(a0, b0), startSec, state.durationSec);

      return { ...state, viewport: { ...state.viewport, startSec, endSec } };
    }

    case "timeline/setSnap": {
      const enabled = action.payload?.enabled ?? state.snap.enabled;

      const modeRaw = action.payload?.mode ?? state.snap.mode;
      const mode = SNAP_MODES.has(String(modeRaw)) ? String(modeRaw) : state.snap.mode;

      const fpsRaw = action.payload?.fps ?? state.snap.fps;
      const fps = finite(fpsRaw) ? clamp(Number(fpsRaw), 1, 240) : state.snap.fps;

      const bpmRaw = action.payload?.bpm ?? state.snap.bpm;
      const bpm = finite(bpmRaw) ? clamp(Number(bpmRaw), 1, 400) : state.snap.bpm;

      const beatDivRaw = action.payload?.beatDiv ?? state.snap.beatDiv;
      const beatDiv = finite(beatDivRaw) ? clamp(Math.round(Number(beatDivRaw)), 1, 32) : state.snap.beatDiv;

      return { ...state, snap: { enabled: !!enabled, mode: mode as any, fps, bpm, beatDiv } };
    }

    case "timeline/setZoom": {
      const pxRaw = a?.payload?.pxPerSec;
      const pxPerSec = finite(pxRaw) ? clamp(Number(pxRaw), 1, 2000) : state.viewport.pxPerSec;
      return { ...state, viewport: { ...state.viewport, pxPerSec } };
    }

    case "timeline/setLaneFocus": {
      const modeRaw = String(a?.payload?.mode ?? state.laneFocus.mode).trim().toLowerCase();
      const mode = modeRaw === "import" ? "import" : "all";
      const visibleTrackIds = Array.isArray(a?.payload?.visibleTrackIds)
        ? a.payload.visibleTrackIds.map((trackId: unknown) => String(trackId ?? "").trim()).filter(Boolean)
        : state.laneFocus.visibleTrackIds;
      const anchorTrackId =
        a?.payload?.anchorTrackId == null
          ? state.laneFocus.anchorTrackId
          : String(a.payload.anchorTrackId ?? "").trim() || null;
      const sourceClipId =
        a?.payload?.sourceClipId == null
          ? state.laneFocus.sourceClipId
          : String(a.payload.sourceClipId ?? "").trim() || null;
      const hideAuxiliaryLanes =
        typeof a?.payload?.hideAuxiliaryLanes === "boolean"
          ? a.payload.hideAuxiliaryLanes
          : state.laneFocus.hideAuxiliaryLanes;

      return {
        ...state,
        laneFocus: {
          mode,
          visibleTrackIds: mode === "all" ? [] : visibleTrackIds,
          anchorTrackId: mode === "all" ? null : anchorTrackId,
          hideAuxiliaryLanes: mode === "all" ? false : hideAuxiliaryLanes,
          sourceClipId: mode === "all" ? null : sourceClipId,
        },
      };
    }

    case "timeline/setLoopSelection": {
      const enabled = !!a?.payload?.enabled;
      return { ...state, loopSelectionEnabled: enabled };
    }

    case "timeline/narrativePreview/set": {
      const compileId = readStr(a?.payload?.compileId) ?? null;
      const blocks = Array.isArray(a?.payload?.blocks) ? a.payload.blocks : [];
      const overlays = Array.isArray(a?.payload?.overlays) ? a.payload.overlays : [];

      return {
        ...state,
        narrativePreview: {
          active: !!compileId,
          compileId,
          blocks,
          overlays,
        },
      };
    }

    case "timeline/narrativePreview/clear": {
      if (!state.narrativePreview.active && !state.narrativePreview.blocks.length && !state.narrativePreview.overlays.length) {
        return state;
      }

      return {
        ...state,
        narrativePreview: {
          active: false,
          compileId: null,
          blocks: [],
          overlays: [],
        },
      };
    }

    /* ---------- Marker ---------- */

    case "marker/add": {
      const raw = (action as any).payload?.marker ?? (action as any).payload ?? null;

      const next = coerceMarker(raw, state.durationSec);
      if (!next) return state;

      const markers = replaceById(state.markers ?? [], next);
      return { ...state, markers };
    }

    case "marker/move": {
      const id = String(action.payload?.markerId ?? "");
      if (!id) return state;

      const timeSec = clamp(
        finite(action.payload?.timeSec) ? Number(action.payload!.timeSec) : 0,
        0,
        state.durationSec,
      );

      const i = (state.markers ?? []).findIndex((m) => m.id === id);
      if (i < 0) return state;

      const fps = clamp(finite(state.snap?.fps) ? Number(state.snap!.fps) : 30, 1, 240);
      const nextFrameIndex = Math.max(0, Math.round(timeSec * fps));
      const markers = (state.markers ?? []).slice();
      const prevMarker = markers[i];
      markers[i] = {
        ...prevMarker,
        timeSec,
        ...(prevMarker.frameIndex != null ? { frameIndex: nextFrameIndex } : {}),
        ...(prevMarker.annotation
          ? {
              annotation: {
                ...prevMarker.annotation,
                ...(prevMarker.annotation.frameIndex != null ? { frameIndex: nextFrameIndex } : {}),
              },
            }
          : {}),
      };
      return { ...state, markers };
    }

    case "marker/remove": {
      const id = String(action.payload?.markerId ?? "");
      if (!id) return state;

      const markers = (state.markers ?? []).filter((m) => m.id !== id);
      if (markers.length === (state.markers ?? []).length) return state;
      return { ...state, markers };
    }

    case "annotation/add": {
      const raw = (action as any).payload?.annotation ?? null;
      const annotation = coerceAnnotation(raw, state.durationSec, action.meta?.ts);
      if (!annotation) return state;

      const annotations = replaceById(state.annotations ?? [], annotation).sort(
        (left: any, right: any) => Number(right.createdAt ?? 0) - Number(left.createdAt ?? 0),
      );

      return { ...state, annotations };
    }

    case "annotation/update": {
      const annotationId = String((action as any).payload?.annotationId ?? "").trim();
      const patch = (action as any).payload?.patch ?? null;
      if (!annotationId || !patch || typeof patch !== "object") return state;

      const existing = (state.annotations ?? []).find((entry) => entry.id === annotationId);
      if (!existing) return state;

      const annotation = coerceAnnotation({ ...existing, ...patch, id: annotationId }, state.durationSec, action.meta?.ts, existing);
      if (!annotation) return state;

      const annotations = replaceById(state.annotations ?? [], annotation).sort(
        (left: any, right: any) => Number(right.createdAt ?? 0) - Number(left.createdAt ?? 0),
      );

      return { ...state, annotations };
    }

    /* ---------- Clip ---------- */

    case "clip/add": {
      const raw = (action as any).payload?.clip ?? (action as any).payload ?? null;

      let next = coerceClip(raw, state.durationSec);
      if (!next) return state;

      // Strict overlap enforcement (track-declared)
      next = enforceStrictNoOverlapForClip(state, next, state.clips ?? []);

      const clips = replaceById(state.clips ?? [], next);

      const selectedClipId =
        state.selectedClipId && clips.some((c) => String(c.id) === String(state.selectedClipId))
          ? state.selectedClipId
          : null;

      return { ...state, clips, selectedClipId };
    }

    case "clip/select": {
      const clipId = (action as any).payload?.clipId;
      return { ...state, selectedClipId: clipId == null ? null : String(clipId) };
    }

    case "clip/patch": {
      const id = String((action as any).payload?.clipId ?? "");
      if (!id) return state;

      const patch = (action as any).payload?.patch ?? {};
      const i = (state.clips ?? []).findIndex((c) => String(c.id) === id);
      if (i < 0) return state;

      const cur = (state.clips ?? [])[i];

      // Important: keep url/mediaId if provided, but don’t erase if undefined
      const nextRaw = { ...cur, ...patch, id: cur.id, trackId: cur.trackId };

      let next = coerceClip(nextRaw, state.durationSec);
      if (!next) return state;

      // If patch changed timing, enforce strict overlap on that track
      const timingChanged =
        patch?.startSec != null || patch?.endSec != null || patch?.durSec != null;

      if (timingChanged) {
        const others = (state.clips ?? []).filter((c) => String(c.id) !== String(id));
        next = enforceStrictNoOverlapForClip(state, next, others);
      }

      const clips = (state.clips ?? []).slice();
      clips[i] = next;

      return { ...state, clips };
    }

    case "clip/remove": {
      const id = String((action as any).payload?.clipId ?? "");
      if (!id) return state;

      const clips = (state.clips ?? []).filter((clip) => String(clip.id) !== id);
      if (clips.length === (state.clips ?? []).length) return state;

      return {
        ...state,
        clips,
        selectedClipId: String(state.selectedClipId ?? "") === id ? null : state.selectedClipId,
      };
    }

    case "clip/move": {
      const id = String((action as any).payload?.clipId ?? "");
      if (!id) return state;

      const i = (state.clips ?? []).findIndex((c) => c.id === id);
      if (i < 0) return state;

      const cur = (state.clips ?? [])[i];
      const len = Math.max(MIN_RANGE_LEN, cur.endSec - cur.startSec);

      const startRaw = (action as any).payload?.startSec;
      const startSec = clamp(
        finite(startRaw) ? Number(startRaw) : cur.startSec,
        0,
        Math.max(0, state.durationSec - len),
      );

      const endSec = clamp(startSec + len, startSec + MIN_RANGE_LEN, state.durationSec);

      let moved = coerceClip({ ...cur, startSec, endSec }, state.durationSec) ?? { ...cur, startSec, endSec };

      // Strict overlap enforcement
      const others = (state.clips ?? []).filter((c) => String(c.id) !== String(id));
      moved = enforceStrictNoOverlapForClip(state, moved, others);

      const clips = (state.clips ?? []).slice();
      clips[i] = moved;

      return { ...state, clips };
    }

    case "clip/resize": {
      const id = String((action as any).payload?.clipId ?? "");
      if (!id) return state;

      const edge = (action as any).payload?.edge as "start" | "end" | undefined;
      const tRaw = (action as any).payload?.timeSec;

      const i = (state.clips ?? []).findIndex((c) => c.id === id);
      if (i < 0) return state;

      const cur = (state.clips ?? [])[i];
      const t = finite(tRaw) ? Number(tRaw) : edge === "start" ? cur.startSec : cur.endSec;

      let startSec = cur.startSec;
      let endSec = cur.endSec;

      if (edge === "start") startSec = t;
      if (edge === "end") endSec = t;

      const norm = normStartEnd(startSec, endSec, state.durationSec);

      let resized = coerceClip({ ...cur, ...norm }, state.durationSec) ?? { ...cur, ...norm };

      // Strict overlap enforcement
      const others = (state.clips ?? []).filter((c) => String(c.id) !== String(id));
      resized = enforceStrictNoOverlapForClip(state, resized, others);

      const clips = (state.clips ?? []).slice();
      clips[i] = resized;

      return { ...state, clips };
    }

    /* ---------- Track helpers (injection support) ---------- */

    case "track/patch": {
      const trackId = String((action as any).payload?.trackId ?? "");
      if (!trackId) return state;

      const patch = (action as any).payload?.patch;
      const tracks = Array.isArray(state.tracks) ? state.tracks.slice() : [];

      const i = tracks.findIndex((t: any) => String(t?.id) === trackId);
      if (i < 0) return state;

      const cur = tracks[i] as any;
      const next = typeof patch === "function" ? patch(cur) : { ...cur, ...(patch || {}) };
      const normalizedNext = {
        ...cur,
        ...next,
        rangeAnchor: coerceRangeAnchorBinding((next as any)?.rangeAnchor) ?? null,
      };

      tracks[i] = normalizedNext;
      return { ...state, tracks };
    }

    case "timeline/setTracks": {
      const incoming = (action as any).payload?.tracks;
      if (!Array.isArray(incoming)) return state;
      // shallow replace tracks (consumer is responsible for clip coercion)
      return { ...state, tracks: incoming.slice() };
    }

    case "clip/beginMove":
    case "clip/commit":
      return state;

    /* ---------- Range ---------- */

    case "range/add": {
      const raw = (action as any).payload?.range ?? (action as any).payload ?? null;

      const next = coerceRange(raw, state.durationSec);
      if (!next) return state;

      const ranges = replaceById(state.ranges ?? [], next);
      const clips = applyRangeFollowers(state, next);

      const selectedRangeId =
        state.selectedRangeId && ranges.some((r) => String(r.id) === String(state.selectedRangeId))
          ? state.selectedRangeId
          : null;

      return { ...state, ranges, clips, selectedRangeId };
    }

    case "range/update": {
      const id = String((action as any).payload?.rangeId ?? "");
      if (!id) return state;

      const i = (state.ranges ?? []).findIndex((r) => String(r.id) === id);
      if (i < 0) return state;

      const cur = (state.ranges ?? [])[i];

      const { startSec, endSec } = normStartEnd(
        (action as any).payload?.startSec,
        (action as any).payload?.endSec,
        state.durationSec,
      );

      const labelMaybe = (action as any).payload?.label;
      const trackIdMaybe = (action as any).payload?.trackId;
      const kindMaybeRaw = (action as any).payload?.kind;

      // ✅ Canonicalization hardening:
      // - If label provided: recompute kind from label (phase:* => phase; else normalize or default)
      // - Else if kind provided: normalize it
      // - Else: keep existing kind (prevents accidental changes)
      let kind = (cur as any).kind;

      if (labelMaybe != null) {
        kind = isPhaseLabel(labelMaybe) ? "phase" : (normalizeKind(kindMaybeRaw) ?? "selection");
      } else if (kindMaybeRaw != null) {
        kind = normalizeKind(kindMaybeRaw) ?? kind;
      }

      const next: TimelineRange = {
        ...cur,
        startSec,
        endSec,
        ...(labelMaybe != null ? { label: String(labelMaybe) } : {}),
        ...(trackIdMaybe != null ? { trackId: String(trackIdMaybe) } : {}),
        ...(kind ? { kind: kind as any } : {}),
      };

      const ranges = (state.ranges ?? []).slice();
      ranges[i] = next;
      const clips = applyRangeFollowers(state, next);

      const selectedRangeId =
        state.selectedRangeId && ranges.some((r) => String(r.id) === String(state.selectedRangeId))
          ? state.selectedRangeId
          : null;

      return { ...state, ranges, clips, selectedRangeId };
    }

    case "range/remove": {
      const id = String((action as any).payload?.rangeId ?? "");
      if (!id) return state;

      const ranges = (state.ranges ?? []).filter((r) => String(r.id) !== id);
      if (ranges.length === (state.ranges ?? []).length) return state;

      const selectedRangeId =
        state.selectedRangeId && ranges.some((r) => String(r.id) === String(state.selectedRangeId))
          ? state.selectedRangeId
          : null;

      return { ...state, ranges, selectedRangeId };
    }

    case "range/select": {
      const rangeId = (action as any).payload?.rangeId;
      return { ...state, selectedRangeId: rangeId == null ? null : String(rangeId) };
    }

    /* ---------- AtomicRuns ---------- */

    case "atomicRuns/setAll": {
      const payload = (action as any).payload ?? {};
      const incoming = Array.isArray(payload?.runs) ? payload.runs : [];
      const cleaned = incoming.map((r: any) => coerceAtomicRun(r, state.durationSec, ts)).filter(Boolean);

      const selectedRunIdRaw =
        typeof state.atomicRuns?.selectedRunId === "string" ? state.atomicRuns.selectedRunId : null;

      const selectedRunId =
        selectedRunIdRaw && cleaned.some((r: any) => String(r?.id) === selectedRunIdRaw) ? selectedRunIdRaw : null;

      return {
        ...state,
        atomicRuns: {
          v: 1,
          updatedAt: finite(ts) ? ts : (finite(state.atomicRuns?.updatedAt) ? Number(state.atomicRuns.updatedAt) : 0),
          runs: cleaned,
          selectedRunId,
        },
      };
    }

    case "atomicRuns/select": {
      const rid = (action as any).payload?.runId;
      const runId = rid == null ? null : String(rid);

      const exists = runId ? (state.atomicRuns?.runs ?? []).some((r: any) => String(r?.id) === runId) : true;

      return {
        ...state,
        atomicRuns: {
          ...state.atomicRuns,
          selectedRunId: exists ? runId : null,
          updatedAt: finite(ts) ? ts : (finite(state.atomicRuns?.updatedAt) ? Number(state.atomicRuns.updatedAt) : 0),
        },
      };
    }

    case "atomicRuns/patch": {
      const runId = String((action as any).payload?.runId ?? "");
      if (!runId) return state;

      const patch = (action as any).payload?.patch ?? {};
      const list = state.atomicRuns?.runs ?? [];
      const i = list.findIndex((r: any) => String(r?.id) === runId);
      if (i < 0) return state;

      const prev = list[i];
      const nextRaw = { ...prev, ...patch, id: runId };

      // ✅ allow action.meta.ts to flow through if updatedAt not explicitly provided
      if (!finite(nextRaw.updatedAt) && finite(ts)) nextRaw.updatedAt = ts;

      const next = coerceAtomicRun(nextRaw, state.durationSec, ts);
      if (!next) return state;

      const runs = list.slice();
      runs[i] = next;

      return {
        ...state,
        atomicRuns: {
          ...state.atomicRuns,
          runs,
          updatedAt: finite(ts) ? ts : (finite(state.atomicRuns?.updatedAt) ? Number(state.atomicRuns.updatedAt) : 0),
        },
      };
    }

    default:
      return state;
  }
}
